import type { ReactNode } from 'react';
import type { AuthState } from '../../types';

import { useSetState } from 'minimal-shared/hooks';
import { useMemo, useEffect, useCallback } from 'react';
import { getCurrentUser, fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';

import { Sentry } from 'src/lib/sentry';

import { AuthContext } from '../auth-context';
import { configureAmplify } from './amplify-config';

// ----------------------------------------------------------------------

type Props = {
  children: ReactNode;
};

export function AmplifyAuthProvider({ children }: Props) {
  const { state, setState } = useSetState<AuthState>({ user: null, loading: true });

  const checkUserSession = useCallback(async () => {
    if (!configureAmplify()) {
      setState({ user: null, loading: false });
      return;
    }

    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const attributes = await fetchUserAttributes();

      const accessToken = session.tokens?.accessToken?.toString();

      Sentry.setUser({
        id: currentUser.userId,
        email: attributes.email,
        username: `${attributes.given_name ?? ''} ${attributes.family_name ?? ''}`.trim() || undefined,
      });

      setState({
        user: {
          ...currentUser,
          ...attributes,
          accessToken,
        },
        loading: false,
      });
    } catch (error) {
      console.error(error);
      Sentry.setUser(null);
      setState({ user: null, loading: false });
    }
  }, [setState]);

  useEffect(() => {
    checkUserSession();
  }, [checkUserSession]);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user ? { ...state.user, role: state.user?.role ?? 'admin' } : null,
      checkUserSession,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
    }),
    [checkUserSession, state.user, status]
  );

  return <AuthContext value={memoizedValue}>{children}</AuthContext>;
}
